import React, { useState } from 'react';
import styled from 'styled-components';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { MessageBar } from '@fluentui/react-components';
import { PrimaryButton, TLInput } from '@repo/ui';

export type FormInputs = {
  companyName: string;
  addressLineOne: string;
  addressLineTwo?: string;
  addressLineThree?: string;
  townCity: string;
  county: string;
  postcode: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  password: string;
  floorPlannerUrl: string;
  openingTimes: string;
};

const formSchema = {
  companyName: yup.string().required('Please enter your company name'),
  addressLineOne: yup.string().required('Please enter your address'),
  addressLineTwo: yup.string(), // Optional field
  addressLineThree: yup.string(), // Optional field
  townCity: yup.string().required('Please enter your town/city'),
  county: yup.string().required('Please enter your county'),
  postcode: yup.string().required('Please enter your postcode'),
  contactName: yup.string().required('Please enter your first name'),
  contactPhone: yup.string().required('Please enter your phone number'),
  contactEmail: yup.string().email().required('Please enter your e-mail address'),
  password: yup.string().required('Please enter your password'),
  floorPlannerUrl: yup.string().required('Please enter your Floor Planner URL'),
  openingTimes: yup.string().required('Please enter your opening times'),
};
interface Props {
  className?: string;
}

const GetStartedPage: React.FC<Props> = ({ className }) => {
  const [serverError] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({
    resolver: yupResolver(yup.object(formSchema)),
  });

  const onSubmit: SubmitHandler<FormInputs> = async formData => {
    console.log(`FORM DATA`, formData);
  };

  return (
    <div className={className}>
      <h1>Get Started</h1>
      <p className="intro">
        Please complete this short form. We are excited to be working with you.
      </p>

      <form onSubmit={handleSubmit(onSubmit)}>
        <fieldset>
          <h2>Company Details</h2>
          <TLInput
            className="field"
            id="txtCompanyName"
            label="Company Name"
            props={{
              type: 'text',
              ...register('companyName'),
            }}
            errorMessage={errors.companyName?.message}
          />
          <TLInput
            className="field-tight"
            id="txtAddressLineOne"
            label="Address"
            props={{
              type: 'text',
              ...register('addressLineOne'),
            }}
          />
          <TLInput
            className="field-tight"
            id="txtAddressLineTwo"
            label=""
            props={{
              type: 'text',
              ...register('addressLineTwo'),
            }}
          />
          <TLInput
            className="field"
            id="txtAddressLineThree"
            label=""
            props={{
              type: 'text',
              ...register('addressLineThree'),
            }}
            errorMessage={errors.addressLineOne?.message}
          />
          <TLInput
            className="field"
            id="txtTownCity"
            label="Town/City"
            props={{
              type: 'text',
              ...register('townCity'),
            }}
            errorMessage={errors.townCity?.message}
          />
          <TLInput
            className="field"
            id="txtCounty"
            label="County"
            props={{
              type: 'text',
              ...register('county'),
            }}
            errorMessage={errors.county?.message}
          />
          <TLInput
            className="field"
            id="txtPostcode"
            label="Postcode"
            props={{
              type: 'text',
              ...register('postcode'),
            }}
            errorMessage={errors.postcode?.message}
          />
        </fieldset>

        <fieldset>
          <h2>Contact Details</h2>
          <TLInput
            className="field"
            id="txtName"
            label="Your Name"
            props={{
              type: 'text',
              ...register('contactName'),
            }}
            errorMessage={errors.contactName?.message}
          />
          <TLInput
            className="field"
            id="txtContactPhone"
            label="Your Phone Number"
            props={{
              type: 'text',
              ...register('contactPhone'),
            }}
            errorMessage={errors.contactPhone?.message}
          />
          <TLInput
            className="field"
            id="txtContactEmail"
            label="Your E-mail Address"
            props={{
              type: 'email',
              ...register('contactEmail'),
            }}
            errorMessage={errors.contactEmail?.message}
          />
        </fieldset>

        <fieldset>
          <h2>Basic Twin Info</h2>
          <div className="input-group">
            <TLInput
              className="field"
              id="txtFloorPlannerUrl"
              label="Floor Planner URL"
              props={{
                type: 'text',
                ...register('floorPlannerUrl'),
              }}
              errorMessage={errors.floorPlannerUrl?.message}
            />
          </div>
          <div className="input-group">
            <TLInput
              className="field"
              id="txtOpeningTimes"
              label="Opening Times"
              props={{
                type: 'text',
                ...register('openingTimes'),
              }}
              errorMessage={errors.openingTimes?.message}
            />
          </div>
        </fieldset>

        <div className="submit-row">
          <PrimaryButton type="submit">Get Started</PrimaryButton>
        </div>
        {serverError ? (
          <MessageBar id={`server-error`} intent="error">
            {serverError}
          </MessageBar>
        ) : null}
      </form>
    </div>
  );
};

export default styled(GetStartedPage)`
  p.intro {
    margin: 1rem 0 2rem 0;
  }

  fieldset {
    margin-bottom: 2rem;
    padding-bottom: 2rem;
    border-bottom: 1px #dad7d6 solid;
  }

  fieldset h2 {
    margin-bottom: 1.25rem;
    font-size: 1.25rem;
  }

  /* Was Tailwind space-y-6 on the form. Reproduced with the exact selector (and
     margin-bottom: 0, which space-y also emits) so the cascade is unchanged: this rule
     (higher specificity than .submit-row and than the fieldset rule) overrides the submit
     row's 4rem top margin to 1.5rem and zeroes later fieldsets' 2rem bottom margin, as
     Tailwind's space-y-6 did. */
  form > :not([hidden]) ~ :not([hidden]) {
    margin-top: 1.5rem;
    margin-bottom: 0;
  }

  /* Was Tailwind mb-4 / mb-2 on TLInput. */
  .field {
    margin-bottom: 1rem;
  }
  .field-tight {
    margin-bottom: 0.5rem;
  }

  /* Was Tailwind mt-2 on the twin-info field wrappers. */
  .input-group {
    margin-top: 0.5rem;
  }

  /* Was Tailwind mt-16 on the submit row (overridden to 1.5rem by the form rule above). */
  .submit-row {
    margin-top: 4rem;
  }
`;
